import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ContentStateProperty, ScreenProperty } from '../../../../../../../@types/types'
import { SCREEN_OTHER_FACILITIES, SCREENS } from '../../../../../../../services/domain/wizardScreens'
import ToggleSwitch from '../../../../../../common/inputs/toggleSwitch/ToggleSwitch'
import { Stepper } from '../../../../../../common/inputs/stepper/Stepper'
import DropdownSelect from '../../../../../../common/inputs/dropdownSelect/DropdownSelect'
import { ParkingSpaceTypeOption } from '../../../../../../../@types/dropdown-types'
import CombinedFieldItem from '../../../../../../common/inputs/combinedFieldItem/CombinedFieldItem'
import { NumberInput } from '../../../../../../common/inputs/numberInput/NumberInput'
import { parseNumber } from '../../../../../../../services/validation/rules/validationFunctions'

const screen = SCREENS[SCREEN_OTHER_FACILITIES]

export interface ParkingSpaceProps {
  screenId: string
  noParkingSpaces?: string
  parkingSpaceRent?: string
  parkingSpaceType?: ParkingSpaceTypeOption
  showParkingSpaceType?: boolean
  hasGarageLabel?: boolean
  parkingSpaceRentTypeLabel?: 'Rent' | 'RentPerItem' | 'RentBuyingPrice'
  noParkingSpacesValidation: string[]
  parkingSpaceRentValidation: string[]
  doReset: (clearingUpOf: ScreenProperty[]) => void
  onChange: (property: ContentStateProperty, value: string | undefined) => void
}

const elementId = 'parking-space'

const ParkingSpace: React.FC<ParkingSpaceProps> = ({
  noParkingSpaces,
  parkingSpaceRent,
  parkingSpaceType,
  showParkingSpaceType = false,
  hasGarageLabel = false,
  parkingSpaceRentTypeLabel = 'Rent',
  noParkingSpacesValidation,
  parkingSpaceRentValidation,
  doReset,
  onChange,
}) => {
  const [hasParkingSpace, setParkingSpace] = useState(
    showParkingSpaceType
      ? !!noParkingSpaces ||
          !!parkingSpaceRent ||
          (parkingSpaceType !== ParkingSpaceTypeOption.NONE && !!parkingSpaceType)
      : !!noParkingSpaces || !!parkingSpaceRent
  )
  const { t } = useTranslation()
  const hasUsedParkingSpace =
    noParkingSpaces || parkingSpaceRent || (parkingSpaceType && parkingSpaceType !== ParkingSpaceTypeOption.NONE)

  useEffect(
    () => (hasUsedParkingSpace ? setParkingSpace(true) : undefined),
    [hasUsedParkingSpace, noParkingSpaces, parkingSpaceRent, parkingSpaceType]
  )

  const doToggle = () => {
    setParkingSpace((prev) => !prev)
    hasParkingSpace && callReset()
  }

  const callReset = () => {
    const resetForNoParkingSpaces: ScreenProperty = {
      screen: screen.name,
      property: 'noParkingSpaces',
    }
    const resetForParkingSpaceRent: ScreenProperty = {
      screen: screen.name,
      property: 'parkingSpaceRent',
    }
    const resetForParkingSpaceType: ScreenProperty = {
      screen: screen.name,
      property: 'parkingSpaceType',
    }
    doReset(
      showParkingSpaceType
        ? [resetForNoParkingSpaces, resetForParkingSpaceRent, resetForParkingSpaceType]
        : [resetForNoParkingSpaces, resetForParkingSpaceRent]
    )
  }

  return (
    <div className='grid-item one-whole'>
      <div className='grid'>
        <div className='grid-item'>
          <ToggleSwitch
            id={elementId}
            alignment='custom'
            propertyName='custom'
            label={
              <span>
                {t(
                  `wizard.screen.otherFacilities.${hasGarageLabel ? 'parkingSpaceGarageVariant' : 'parkingSpace'}`,
                  'Parking Space'
                )}
              </span>
            }
            value={hasParkingSpace}
            onChange={doToggle}
          />
        </div>
        {hasParkingSpace && (
          <>
            <CombinedFieldItem
              id='no-parking-spaces'
              label={t('wizard.screen.otherFacilities.noParkingSpaces', 'Number')}
              children={
                <Stepper
                  id='no-parking-spaces'
                  propertyName='noParkingSpaces'
                  placeholder=''
                  label=''
                  input={noParkingSpaces}
                  type='tel'
                  numericOnly
                  maxLength={4}
                  max={999}
                  errors={noParkingSpacesValidation}
                  doOnFieldChange={(property, value) => onChange(property, value !== '' ? value : undefined)}
                />
              }
            />

            <CombinedFieldItem
              id='parking-space-rent'
              label={t(`wizard.screen.otherFacilities.parkingSpace${parkingSpaceRentTypeLabel}`, 'Rent')}
              children={
                <NumberInput
                  id='parking-space-rent'
                  propertyName='parkingSpaceRent'
                  placeholder=''
                  label=''
                  value={parkingSpaceRent ? parseNumber(parkingSpaceRent) : undefined}
                  minPrecision={0}
                  maxPrecision={2}
                  affixRight='unit'
                  unitRight='eur'
                  maxLength={16}
                  errors={parkingSpaceRentValidation}
                  doOnFieldChange={(property, value) =>
                    onChange(property, value ? value.toString().replace('.', ',') : undefined)
                  }
                />
              }
            />

            {showParkingSpaceType && (
              <CombinedFieldItem
                id='parking-space-type'
                label={t('wizard.screen.otherFacilities.parkingSpaceType', 'Rent')}
                children={
                  <DropdownSelect
                    id='parking-space-type'
                    name='parkingSpaceType'
                    value={parkingSpaceType}
                    noInformation={ParkingSpaceTypeOption.NONE}
                    options={{
                      translationKeyGroup: 'wizard.screen.otherFacilities.parkingSpaceTypes',
                      options: Object.keys(ParkingSpaceTypeOption),
                    }}
                    onChange={(value) => onChange('parkingSpaceType', value.value!)}
                    allowEmpty
                  />
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ParkingSpace
